"use client";

import { Box, Paper, Divider, Typography } from "@mui/material";
import TitleTypography from "./TitleTypography";
import InputField from "./InputField";
import DomainSelect from "./DomainSelect";
import AuthButton from "./AuthButton";
import ErrorAlert from "./ErrorAlert";
import { useLoginForm } from "@/app/hooks/useLoginForm";
import { useSession } from "next-auth/react";
import { useEffect } from "react";
import { useRouter } from "next/navigation";
import { useSearchParams } from "next/navigation";

// Import dos componentes de loading
import {
  SSOLoading,
  GovBRLoading,
  GSILoading,
  InitialLoading,
} from "../loading/AuthLoading";
import FormLoading from "../loading/FormLoading";


export default function LoginForm() {
  const {
    email,
    password,
    showPassword,
    selectedDomain,
    auth,
    domains,
    loadingDomains,
    handleChange,
    handleGSI,
    handleGovBrLogin,
    togglePasswordVisibility,
  } = useLoginForm();

  const { data: session, status } = useSession();
  const searchParams = useSearchParams();
  const errorParam = searchParams ? searchParams.get("error") : null;
  const unauthorizedMessage = errorParam
    ? errorParam
    : "N\u00E3o autorizado. Fa\u00E7a login para acessar relat\u00F3rios.";

  const globalError = errorParam
    ? unauthorizedMessage
    : auth.gsi.error || auth.govbr.error || auth.sso.error || "";

  const router = useRouter();

  // Redirecionar para o dashboard caso o usuário esteja logado
  useEffect(() => {
    if (status === "authenticated") {
      router.push("/dashboard");
    }
  }, [status, router]);

  // Loading durante verificação inicial
  // initialCheck removed — initial loading is handled via session status or the hook's flags
  // Loading durante autenticação SSO
  if (auth.sso.loading) {
    return <SSOLoading />;
  }

  // Loading durante autenticação GovBR
  if (auth.govbr.loading) {
    return <GovBRLoading />;
  }

  // Loading durante autenticação GSI tradicional
  if (auth.gsi.loading) {
    return <GSILoading />;
  }

  // Se já estiver autenticado, não renderiza o formulário
  if (status === "authenticated") {
    return null;
  }

  // Loading durante carregamento de domínios (mas mostra o formulário)
  if (loadingDomains) {
    return (
      <Paper
        sx={{
          p: 4,
          width: "100%",
          maxWidth: 450,
          minWidth: 300,
          borderRadius: 3,
          mx: "auto",
          boxShadow: "10px 4px 12px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Box component="form" onSubmit={() => {}} sx={{ width: "100%" }}>
          <TitleTypography variant="h4" dataTestId="login-title">
            Área de Login
          </TitleTypography>

          <FormLoading
            title="Carregando domínios"
            message="Buscando domínios disponíveis..."
            type="linear"
          />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        p: 4,
        width: "100%",
        maxWidth: 450,
        minWidth: 300,
        borderRadius: 3,
        mx: "auto",
        boxShadow: "10px 4px 12px rgba(0, 0, 0, 0.1)",
      }}
    >
      <Box component="form" onSubmit={() => {}} sx={{ width: "100%" }}>
        <TitleTypography variant="h4" dataTestId="login-title">
          Área de Login
        </TitleTypography>

        <Typography
          variant="body1"
          sx={{ textAlign: "center", mb: 2, color: "text.secondary" }}
        >
          Acesse o Monitor com:
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <AuthButton
            onClick={handleGovBrLogin}
            loading={auth.govbr.loading}
            label="Entrar com gov.br"
            isGovButton
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography
          variant="body1"
          sx={{ textAlign: "center", mb: 2, color: "text.secondary" }}
        >
          Ou use login de rede
        </Typography>

        <InputField
          label="Usuário"
          type="email"
          value={email}
          onChange={(e) => handleChange(e, "email")}
          placeholder="usuario"
        />

        <DomainSelect
          domains={domains}
          loading={loadingDomains}
          selectedDomain={selectedDomain}
          onChange={(e) => handleChange(e, "domain")}
        />

        <InputField
          label="Senha"
          type={showPassword ? "text" : "password"}
          value={password}
          onChange={(e) => handleChange(e, "password")}
          placeholder="••••••"
          showPasswordToggle={true}
          onTogglePasswordVisibility={togglePasswordVisibility}
        />

        <AuthButton
          onClick={handleGSI}
          loading={auth.gsi.loading}
          label="Entrar"
          isGovButton={false}
        />

        {globalError && <ErrorAlert message={globalError} />}
      </Box>
    </Paper>
  );
}
